import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ConfigurableFormComponent } from '../../../../../shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '../../../../../shared/modules/configurable-form/models/on-field-changed';
import { SpecFieldNames } from '../../constants/specification-field-names.enum';
import { UnloadTypePumpCodes } from '@ordersnproducts/modules/shared/models/pump-type.enum';
import { Store } from '@ngrx/store';
import { getFirstCalendar, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { filter, map, take } from 'rxjs/operators';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

@Injectable()
export class HandlePumpWithLinearMetersCommand implements OnFieldChanged {
  private pumpCodes: string[] = Object.keys(UnloadTypePumpCodes).map((code) => UnloadTypePumpCodes[code]);

  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(value: string, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    this.resetPumpForm(formGroup);
    this.disableAndHide(SpecFieldNames.PUMP_LENGTH, form, true);
    const shouldHidePump = !this.shouldShowPump(String(value).toUpperCase());

    if (shouldHidePump) {
      // Reset values
      form.setValue(SpecFieldNames.PUMP, undefined);
      form.setValue(SpecFieldNames.PIPE_QUANTITY, undefined);
      form.setValue(SpecFieldNames.HOSES, undefined);
      form.setValue(SpecFieldNames.PUMP_LENGTH, undefined);
      form.setValue(SpecFieldNames.LINEAR_METERS, undefined);
    }

    // Hide / show
    this.disableAndHide(SpecFieldNames.PUMP, form, shouldHidePump);
    this.disableAndHide(SpecFieldNames.PIPE_QUANTITY, form, shouldHidePump);
    this.disableAndHide(SpecFieldNames.HOSES, form, shouldHidePump);

    // Handle linear meters
    if (
      [UnloadTypePumpCodes.PUMP, UnloadTypePumpCodes.BOOM_PUMP, UnloadTypePumpCodes.STATIONARY_PUMP].includes(
        value as UnloadTypePumpCodes,
      )
    ) {
      this.store
        .select(getFirstCalendar)
        .pipe(
          filter((calendar) => !!calendar),
          take(1),
          map((calendar) => _safe(() => calendar.pump.flagLinearMeters), false),
        )
        .subscribe((hasLinearMeters) => {
          if (hasLinearMeters) {
            // Show linear meters
            this.disableAndHide(SpecFieldNames.LINEAR_METERS, form, false);
            this.disableAndHide(SpecFieldNames.PIPE_QUANTITY, form, true);
            this.disableAndHide(SpecFieldNames.HOSES, form, true);
          } else {
            // Show pipes and hoses
            this.disableAndHide(SpecFieldNames.LINEAR_METERS, form, true);
            this.disableAndHide(SpecFieldNames.PIPE_QUANTITY, form, false);
            this.disableAndHide(SpecFieldNames.HOSES, form, false);
          }
        });
    } else {
      // hde pipes and hoses
      this.disableAndHide(SpecFieldNames.LINEAR_METERS, form, true);
      this.disableAndHide(SpecFieldNames.PIPE_QUANTITY, form, true);
      this.disableAndHide(SpecFieldNames.HOSES, form, true);
    }
  }

  private shouldShowPump(value: string): boolean {
    return this.pumpCodes.includes(value);
  }

  private disableAndHide(field: SpecFieldNames, form: ConfigurableFormComponent, shouldHide: boolean) {
    form.setDisabled(field, shouldHide);
    form.setHidden(field, shouldHide);
  }

  private resetPumpForm(formGroup: UntypedFormGroup): void {
    formGroup.get(SpecFieldNames.PUMP).setValue(undefined);
    formGroup.get(SpecFieldNames.PIPE_QUANTITY).setValue(0);
    formGroup.get(SpecFieldNames.HOSES).setValue(0);
    formGroup.get(SpecFieldNames.PUMP_LENGTH).setValue(undefined);
    formGroup.get(SpecFieldNames.LINEAR_METERS).setValue(0);
  }
}
